import { useCallback, useEffect, useRef, useState } from 'react';
import type { SectionDef } from './SectionNav';

// Walk up the DOM from el and return the first ancestor whose overflow-y is
// auto/scroll AND whose content overflows its box (i.e. it actually scrolls).
function getScroller(el: HTMLElement): HTMLElement {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return document.documentElement as unknown as HTMLElement;
}

// Ease-in-out cubic: smooth start and end.
function easeInOut(p: number): number {
  return p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
}

function animScroll(scroller: HTMLElement, targetTop: number, onDone: () => void): () => void {
  const start = scroller.scrollTop;
  const distance = targetTop - start;
  if (Math.abs(distance) < 1) {
    onDone();
    return () => {};
  }

  // Duration scales with distance but is capped so short hops feel snappy.
  const duration = Math.min(400, Math.abs(distance) * 0.6);
  let startTime: number | null = null;
  let rafId: number;

  const step = (ts: number) => {
    if (startTime === null) startTime = ts;
    const p = Math.min((ts - startTime) / duration, 1);
    scroller.scrollTop = start + distance * easeInOut(p);
    if (p < 1) {
      rafId = requestAnimationFrame(step);
    } else {
      onDone();
    }
  };

  rafId = requestAnimationFrame(step);
  return () => cancelAnimationFrame(rafId);
}

// How far from the top of the scroll container a section's top edge must be
// before it's considered "active".
const SCROLL_SPY_THRESHOLD = 140;

export interface UseSectionNavResult {
  /** Mutable map of section id → DOM element. Assign via ref callbacks. */
  sectionRefs: React.MutableRefObject<Record<string, HTMLElement | null>>;
  /** Id of the section currently in view. */
  active: string;
  /** Smooth-scrolls to the section and immediately marks it active. */
  scrollToSec: (id: string) => void;
}

export function useSectionNav(sections: SectionDef[]): UseSectionNavResult {
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const [active, setActive] = useState<string>(sections[0]?.id ?? '');
  // Suppresses scroll-spy while a programmatic scroll animation is running.
  const isProgrammaticScroll = useRef(false);
  const cancelScroll = useRef<() => void>(() => {});

  const scrollToSec = useCallback((id: string) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    const scroller = getScroller(el);
    const scrollerTop = scroller.getBoundingClientRect().top;
    const elTop = el.getBoundingClientRect().top;
    const target = Math.max(0, elTop - scrollerTop + scroller.scrollTop - 16);
    cancelScroll.current();
    isProgrammaticScroll.current = true;
    cancelScroll.current = animScroll(scroller, target, () => {
      isProgrammaticScroll.current = false;
    });
    setActive(id);
  }, []);

  useEffect(() => () => cancelScroll.current(), []);

  // Scroll-spy: re-attach whenever the section list changes.
  useEffect(() => {
    // Find any mounted section element to locate the scroller.
    const firstEl = sections.map((s) => sectionRefs.current[s.id]).find(Boolean);
    if (!firstEl) return;

    const scroller = getScroller(firstEl);

    const onScroll = () => {
      if (isProgrammaticScroll.current) return;
      const scrollerTop = scroller.getBoundingClientRect().top;
      let hit = sections[0]?.id ?? '';
      for (const sec of sections) {
        const el = sectionRefs.current[sec.id];
        if (!el) continue;
        if (el.getBoundingClientRect().top - scrollerTop <= SCROLL_SPY_THRESHOLD) {
          hit = sec.id;
        }
      }
      setActive(hit);
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [sections]);

  return { sectionRefs, active, scrollToSec };
}
